/**
 * Juni-2026 (User-Wunsch) — Verlauf & Rückgängig (Undo) der Gruppenbearbeitung.
 *
 * Zeigt die letzten N Bulk-Aktionen des Händlers. Jede Aktion lässt sich
 * rückgängig machen (stellt die gespeicherten alten Feldwerte wieder her).
 */
import { useState, useEffect, useCallback } from "react";
import { X, Loader2, History, RotateCcw, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";

const FIELD_LABELS = {
    dispo_internal_note: "Interne Vermerke",
    model: "Hersteller-Modell",
    color: "Hersteller-Farbe",
    color_base: "Farbbasis",
    price: "Verkaufspreis",
    title: "Titel",
    dispo_order_code: "Hersteller-Bestellcode",
    site_code: "Standort",
    stand_platz: "Standplatz",
    key_depot: "Schlüsseldepot",
    sold: "Status",
    reserved_by: "Status",
    status: "Status",
    reservation_status: "Status",
    reserved_at: "Status",
    reserved_until: "Status",
};

const fmtDT = (iso) => { try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; } };

function labelFields(fields) {
    const seen = new Set();
    const out = [];
    for (const f of fields || []) {
        const lbl = FIELD_LABELS[f] || f;
        if (!seen.has(lbl)) { seen.add(lbl); out.push(lbl); }
    }
    return out;
}

export default function DispoBulkHistoryModal({ onClose, onUndone }) {
    const [items, setItems] = useState(null);
    const [undoingId, setUndoingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/dealer/dispo/bulk-history?limit=30");
            setItems(r.data?.items || []);
        } catch {
            setItems([]);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const undo = async (hid) => {
        setUndoingId(hid);
        try {
            const r = await api.post(`/dealer/dispo/bulk-history/${hid}/undo`);
            toast.success(`${r.data?.restored ?? 0} Fahrzeug(e) wiederhergestellt.`);
            await load();
            onUndone?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Rückgängig fehlgeschlagen.");
        } finally {
            setUndoingId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            data-testid="dispo-bulk-history-modal"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
            <div className="w-full max-w-2xl mt-10 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black flex items-center gap-2">
                        <History className="w-5 h-5 text-[#FFCC00]" /> Verlauf der Gruppenbearbeitung
                    </h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" aria-label="Schließen" data-testid="dispo-bulk-history-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-5 max-h-[65vh] overflow-y-auto">
                    {items === null && <div className="text-center py-8 text-gray-400"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>}
                    {items && items.length === 0 && (
                        <div className="text-center py-8 text-gray-500 text-sm" data-testid="dispo-bulk-history-empty">
                            Noch keine Gruppenbearbeitungen vorhanden.
                        </div>
                    )}
                    <ul className="space-y-2">
                        {(items || []).map((h) => (
                            <li key={h.id} className={`border rounded-sm p-3 ${h.undone ? "border-gray-200 bg-gray-50 opacity-70" : "border-gray-200"}`} data-testid={`dispo-bulk-history-row-${h.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-xs text-gray-500">{fmtDT(h.at)} · {h.actor_label || "—"}</div>
                                        <div className="text-sm font-bold text-[#111827] mt-0.5">
                                            {h.count} Fahrzeug{h.count === 1 ? "" : "e"} · {labelFields(h.fields).join(", ") || "—"}
                                        </div>
                                        {h.titles && h.titles.length > 0 && (
                                            <div className="text-[11px] text-gray-500 mt-0.5 truncate">{h.titles.join(" · ")}</div>
                                        )}
                                    </div>
                                    <div className="shrink-0">
                                        {h.undone ? (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-gray-200 text-gray-600" data-testid={`dispo-bulk-history-undone-${h.id}`}>
                                                <Check className="w-3 h-3" /> Rückgängig gemacht
                                            </span>
                                        ) : (
                                            <Button type="button" onClick={() => undo(h.id)} disabled={undoingId === h.id}
                                                variant="outline" className="h-8 rounded-sm text-[11px] font-bold uppercase tracking-wider border-red-300 text-red-700 hover:bg-red-50"
                                                data-testid={`dispo-bulk-history-undo-${h.id}`}>
                                                {undoingId === h.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RotateCcw className="w-3.5 h-3.5 mr-1" /> Rückgängig</>}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
